import { useMemo } from 'react'
import { Typography } from '@cannect/new-components/atoms'
import { useLocation } from 'react-router-dom'
import defaultImage from '@cannect/assets/img/brandBanner/defaultImage.png'
import { useGetActiveBrands } from '@cannect/services/resources/brands'

type TBrandBannerData = {
  title: string
  subtitle?: string
  logoUrl?: string
  imageUrl?: string
  descriptionCols: string[]
  titleColor?: string
  subtitleColor?: string
}

export const BrandBanner = () => {
  const location = useLocation()
  const { data: brandsData, isLoading: brandsLoading } = useGetActiveBrands()
  const params = new URLSearchParams(location.search)
  const brandId = params.get('brand_id') || ''

  const state = (location.state || {}) as {
    brand_name?: string
    logo?: string
    banner_bg_url?: string
    banner_title?: string
    banner_subtitle?: string
    description_cols?: string[]
    use_logo?: boolean | null
    banner_title_color?: string | null
    banner_subtitle_color?: string | null
  }

  const DEFAULT_BANNER: TBrandBannerData = {
    title: '',
    subtitle: '',
    logoUrl: '',
    imageUrl: defaultImage,
    descriptionCols: []
  }

  const bannerData: TBrandBannerData | null = useMemo(() => {
    if (!brandId) return null
    const {
      banner_title,
      logo,
      banner_bg_url,
      banner_subtitle,
      description_cols: stateDescriptionCols,
      banner_title_color: stateTitleColor,
      banner_subtitle_color: stateSubtitleColor
    } = state

    const activeBrand = brandsData?.brands?.find((brand) => String(brand.id) === String(brandId))
    const brandInfos = (activeBrand?.brandInfos || {}) as {
      banner_title?: string | null
      banner_subtitle?: string | null
      banner_bg_url?: string | null
      logo?: string | null
      alternativeLogo?: string | null
      description_cols?: string[] | null
      use_logo?: boolean | null
      banner_title_color?: string | null
      banner_subtitle_color?: string | null
    }

    const showLogo = (brandInfos.use_logo ?? state.use_logo ?? true) !== false
    const rawTitle = banner_title || brandInfos.banner_title || ''
    const rawSubtitle = banner_subtitle || brandInfos.banner_subtitle || ''
    const isBrandNameOnly =
      Boolean(activeBrand?.name) &&
      rawTitle.trim().toLowerCase() ===
        String(activeBrand?.name || '')
          .trim()
          .toLowerCase()
    const title = showLogo && isBrandNameOnly && !rawSubtitle ? '' : rawTitle
    const subtitle = rawSubtitle
    const imageUrl = banner_bg_url || brandInfos.banner_bg_url || DEFAULT_BANNER.imageUrl
    const logoUrl = showLogo ? logo || brandInfos.logo || brandInfos.alternativeLogo || DEFAULT_BANNER.logoUrl : ''
    let { descriptionCols } = DEFAULT_BANNER
    if (brandInfos.description_cols && brandInfos.description_cols.length > 0) {
      descriptionCols = brandInfos.description_cols
    }
    if (stateDescriptionCols && stateDescriptionCols.length > 0) {
      descriptionCols = stateDescriptionCols
    }
    const titleColor = stateTitleColor || brandInfos.banner_title_color || undefined
    const subtitleColor = stateSubtitleColor || brandInfos.banner_subtitle_color || undefined

    const bannerPayload = { title, subtitle, imageUrl, logoUrl, descriptionCols, titleColor, subtitleColor }
    // eslint-disable-next-line no-console
    console.log('[BrandBanner] bannerData', {
      brandId,
      showLogo,
      isBrandNameOnly,
      ...bannerPayload
    })

    return bannerPayload
  }, [brandId, state, brandsData?.brands])

  if (!brandId) return null

  // Enquanto a API carrega e não temos dados do state (acesso direto/refresh),
  // mostramos um skeleton para evitar o flash da imagem default.
  const hasStateImage = Boolean(state.banner_bg_url)
  if (brandsLoading && !hasStateImage) {
    return <div className="mb-8 h-[445px] w-full animate-pulse rounded-2xl bg-neutral-200" />
  }

  if (!bannerData) return null

  return (
    <div className="mb-8 overflow-hidden rounded-2xl">
      <div className="relative h-[445px] w-full">
        {bannerData.imageUrl && (
          <img
            src={bannerData.imageUrl}
            alt={`Banner da marca ${bannerData.title}`}
            className="absolute inset-0 h-full w-full rounded-b-2xl object-right-top"
            loading="eager"
            fetchPriority="high"
          />
        )}

        {(bannerData.logoUrl || bannerData.subtitle) && (
          <div className="relative z-10 flex h-full items-center px-4 md:px-6">
            <div className="w-full max-w-md space-y-5">
              {bannerData.logoUrl && <img src={bannerData.logoUrl} alt="Logo da marca" className="h-9 w-fit" />}
              {bannerData.title && (
                <Typography.Heading
                  type="headingThree"
                  className={`mt-2 max-w-sm leading-tight md:max-w-md ${!bannerData.titleColor ? 'text-neutral-900' : ''}`}
                  style={bannerData.titleColor ? { color: bannerData.titleColor } : undefined}>
                  {bannerData.title}
                </Typography.Heading>
              )}
              {bannerData.subtitle && (
                <Typography.Text
                  className={`mt-2 max-w-xs text-sm md:max-w-sm ${!bannerData.subtitleColor ? 'text-neutral-800' : ''}`}
                  style={bannerData.subtitleColor ? { color: bannerData.subtitleColor } : undefined}>
                  {bannerData.subtitle}
                </Typography.Text>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Sobre a marca abaixo da imagem */}
      {bannerData.descriptionCols.length > 0 && (
        <div className="py-10">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {bannerData.descriptionCols.map((text, idx) => (
              <div key={idx}>
                {idx === 0 && (
                  <Typography.Heading type="headingSix" className="text-neutral-800">
                    Sobre a marca
                  </Typography.Heading>
                )}
                <Typography.Text className={`text-xs text-neutral-800 ${idx === 0 ? 'mt-2' : ''}`}>
                  {text}
                </Typography.Text>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default BrandBanner
